import ReactTooltip from 'react-tooltip'
import { PoolCardV2 } from 'symbiosis-uikit'
import Spinner from '~/components/Spinner'
import Typography from '~/components/Typography'
import { useGetOriginLikeToken } from '~/hooks/useGetOriginLikeToken'
import { renderAmount } from '~/lib/helpers'
import { useFarmingAPRV2 } from '~/lib/useFarmingAPRV2'
import { useWallet } from '~/lib/useWallet'
import { OmniPoolWithReward } from '../types'
import styles from './FarmList.pcss'
import { useUserLiquidity } from './useUserLiquidity'
import { useEffect, useMemo, useState } from 'react'
import { CROSSCHAIN_CONFIG } from '~/lib/config'
import { TokenAmount } from 'symbiosis-js-sdk'

interface Props {
	data: Array<OmniPoolWithReward>
	onFarmClick: (data: OmniPoolWithReward) => void
}

function FarmList({ data }: Props) {
	const farmingAPR = useFarmingAPRV2()
	const { account } = useWallet()
	const [balanceLoading, amounts] = useUserLiquidity(account)
	const [apr, setApr] = useState<any[]>([])

	useEffect(() => {
		;(async () => {
			const url = `${CROSSCHAIN_CONFIG.api.url}/v1/octo-pool-apr`
			const response = await fetch(url)

			if (response.status !== 200) {
				throw new Error(response.statusText)
			}
			const json = await response.json()
			setApr(json.aprData)
		})()
	}, [])

	const poolsWithUserBalances = useMemo(() => {
		console.log({ balanceLoading, amounts })
		if (balanceLoading) {
			return data
		}
		return data
			.map((i) => {
				let balance = amounts.find(
					(tokenAmount) =>
						tokenAmount.token.address === i.pool.token.address &&
						tokenAmount.token.chainId === i.pool.token.chainId
				)
				if (!balance) {
					balance = new TokenAmount(i.pool.token, '0')
				}
				return {
					...i,
					balance,
				}
			})
			.sort((a, b) => {
				if (a.balance.greaterThan(b.balance)) {
					return -1
				} else if (b.balance.greaterThan(a.balance)) {
					return 1
				} else {
					return 0
				}
			})
	}, [data, amounts, balanceLoading])

	return (
		<div className={styles.container}>
			<ReactTooltip
				id="sis"
				place="bottom"
				effect="float"
				className={styles.aprTooltip}
				arrowColor="transparent"
				offset={{
					right: 122,
				}}
			>
				<Typography size="XS">Farming rewards: SIS</Typography>
			</ReactTooltip>

			{poolsWithUserBalances.map((item) => {
				const { token } = item.pool

				const tokenLikeOrigin = useGetOriginLikeToken(token)

				const fromChainId = token.chainFromId ?? token.chainId
				const baseApr = apr.find((i: any) => {
					return (
						i.chainId === item.pool.chain.id &&
						i.token.toLowerCase() === token.address.toLowerCase()
					)
				})?.apr
				let boostedApr = farmingAPR.boostedApy(
					fromChainId,
					token.chainId
				)

				if (baseApr) {
					boostedApr = (boostedApr || 0) + baseApr
				}

				let baseAprNode: React.ReactNode
				let boostedAprNode: React.ReactNode
				if (farmingAPR.status === 'pending') {
					baseAprNode = <Spinner />
					boostedAprNode = <Spinner />
				} else {
					baseAprNode = (
						<span>
							{baseApr ? `${renderAmount(baseApr, 2)}%` : '-'}
						</span>
					)
					boostedAprNode = (
						<span>
							{boostedApr
								? `${renderAmount(boostedApr, 2)}%`
								: '-'}
						</span>
					)
				}

				let value: React.ReactNode
				if (balanceLoading) {
					value = <Spinner />
				} else {
					value = (
						<span>
							$
							{item.balance
								? `${renderAmount(item.balance.toFixed(), 2)}`
								: '0'}
						</span>
					)
				}

				return (
					<PoolCardV2
						key={`${item.pool.token.address}-${item.pool.token.chainId}`}
						baseAPR={baseAprNode}
						boostedAPR={boostedAprNode}
						token={tokenLikeOrigin}
						value={value}
						valueLabel="Your pool tokens"
					/>
				)
			})}
		</div>
	)
}

export default FarmList
